/**
 * Translated error text for chat actions (de-slop CHAT-2 / CHAT-19).
 *
 * The unified AI endpoint returns English-only `detail` strings, and fetch
 * failures surface as technical text ("Failed to fetch", "HTTP 502"). Users
 * must never see either: map the HTTP status (attached to the thrown Error by
 * `aiAPI.sendMessage`, or an axios `response.status`) plus a few known detail
 * phrases to translation keys. Pure module so it can be unit-tested.
 */
import { userFacingErrorKey } from '../../services/errorMessages';
import { FOLLOWUP_CREDIT_REQUIRED_CODE, noCreditErrorKey } from '../../utils/creditErrors';
import { JOB_ERROR_CODES } from '../../components/voice/jobProgress';

type TFn = (key: string, defaultOrVars?: string | Record<string, unknown>, vars?: Record<string, unknown>) => string;

export function errorStatusOf(error: unknown): number | undefined {
  const e = error as { status?: unknown; response?: { status?: unknown } } | null | undefined;
  const status = e?.status ?? e?.response?.status;
  return typeof status === 'number' ? status : undefined;
}

/** Coded blocks from `/api/ai/message` (structured `detail.code`). */
export const TRIAL_CHAT_LIMIT_REACHED_CODE = 'trial_chat_limit_reached';
/** JOB-R24: an analysis of a stored document with no transcript (422). */
export const DOCUMENT_HAS_NO_TRANSCRIPT_CODE = 'document_has_no_transcript';
export { FOLLOWUP_CREDIT_REQUIRED_CODE };

type CodedDetail = { code: string; limit?: unknown; plan_starts_at?: unknown };

/**
 * The structured `{ code, ... }` detail on any error shape the app produces:
 * `apiErrorFromBody` errors (`.detail`), axios errors
 * (`.response.data.detail`) or a raw response body.
 */
export function codedDetailOf(error: unknown): CodedDetail | null {
  const e = error as { detail?: unknown; response?: { data?: { detail?: unknown } } } | null | undefined;
  for (const candidate of [e?.detail, e?.response?.data?.detail]) {
    if (candidate && typeof candidate === 'object' && typeof (candidate as CodedDetail).code === 'string') {
      return candidate as CodedDetail;
    }
  }
  return null;
}

function errorDetailOf(error: unknown): string {
  const e = error as { message?: unknown; response?: { data?: { detail?: unknown } } } | null | undefined;
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return typeof e?.message === 'string' ? e.message : String(error ?? '');
}

/**
 * Translation key for a failed call to the unified AI endpoint (chat send,
 * tool submit, analysis upload). `fallbackKey` is used when nothing more
 * specific applies.
 */
export function chatSendErrorKey(error: unknown, fallbackKey = 'chat.sendFailed'): string {
  const coded = codedDetailOf(error);
  if (coded?.code === TRIAL_CHAT_LIMIT_REACHED_CODE) {
    // Card-backed trials already chose a plan: chat unlocks when it starts.
    return coded.plan_starts_at
      ? 'chatLibrary.errors.trialChatLimitReachedPlanStarts'
      : 'chatLibrary.errors.trialChatLimitReached';
  }
  if (coded?.code === DOCUMENT_HAS_NO_TRANSCRIPT_CODE) return 'chatLibrary.errors.documentHasNoTranscript';
  // No credit for an analysis / re-assessment: 402 with a coded detail,
  // shared with ICF progress re-runs.
  const creditKey = noCreditErrorKey(error);
  if (creditKey) return creditKey;
  // A failed chat task carries the job's stable code (JOB-R23: the AI
  // provider is out of credit -> "temporary problem on our side, nothing was
  // charged"); show its localized message, never the English task text.
  const jobCode = (error as { code?: unknown } | null | undefined)?.code;
  if (typeof jobCode === 'string' && (JOB_ERROR_CODES as readonly string[]).includes(jobCode)) {
    return `jobs.errors.${jobCode}`;
  }

  const status = errorStatusOf(error);
  const detail = errorDetailOf(error).toLowerCase();

  if (status === 429) {
    return detail.includes('quota') || detail.includes('limit') || detail.includes('credit')
      ? 'chatLibrary.errors.sendLimitReached'
      : 'errors.rateLimited';
  }
  if (status === 402) return 'chatLibrary.errors.sendNeedsSubscription';
  if (status === 403) {
    return detail.includes('verify your email')
      ? 'chatLibrary.errors.sendVerifyEmail'
      : 'chatLibrary.errors.sendNeedsSubscription';
  }
  if (status === 400 && detail.includes('too long')) return 'chatLibrary.errors.sendTooLong';
  if (status === 401) return 'errors.sessionExpired';
  if (status !== undefined && status >= 500) return 'errors.serverUnavailable';

  const generic = userFacingErrorKey(detail);
  return generic === 'errors.genericRetry' ? fallbackKey : generic;
}

export function chatSendErrorMessage(t: TFn, error: unknown, fallbackKey?: string): string {
  const key = chatSendErrorKey(error, fallbackKey);
  const limit = codedDetailOf(error)?.limit;
  return typeof limit === 'number' ? t(key, { limit }) : t(key);
}

/**
 * Translated text for any other chat/library action (attach, remove, rename,
 * delete, download). Never shows backend or network text; `fallbackKey` is
 * the action-specific message.
 */
export function actionErrorMessage(t: TFn, error: unknown, fallbackKey: string): string {
  const status = errorStatusOf(error);
  if (status === 401) return t('errors.sessionExpired');
  if (status === 429) return t('errors.rateLimited');
  if (status !== undefined && status >= 500) return t('errors.serverUnavailable');
  const generic = userFacingErrorKey(errorDetailOf(error));
  return generic === 'errors.genericRetry' ? t(fallbackKey) : t(generic);
}

/** Reference-file attach (`POST /api/chat/sessions/{id}/attach-context`). */
export function referenceAttachErrorMessage(t: TFn, error: unknown): string {
  const status = errorStatusOf(error);
  const detail = errorDetailOf(error).toLowerCase();
  if (status === 413 || detail.includes('too large')) return t('chatLibrary.errors.fileTooLarge');
  if (status === 400 || status === 415) {
    if (detail.includes('unsupported')) return t('chatLibrary.errors.fileUnsupported');
    if (detail.includes('extract') || detail.includes('no readable text')) return t('chatLibrary.errors.fileNoText');
    if (detail.includes('already has')) return t('chatLibrary.errors.referenceTooManyFiles');
    if (detail.includes('full of reference text')) return t('chatLibrary.errors.referenceSessionFull');
  }
  return actionErrorMessage(t, error, 'chat.referenceAttachFailed');
}

/**
 * Text to put back in the composer after a failed send (CHAT-2). The input is
 * read-only while sending, so it is normally empty; if the user somehow typed
 * something new, keep that rather than overwrite it.
 */
export function restoreDraftAfterFailure(currentInput: string, sentText: string): string {
  return currentInput.trim().length > 0 ? currentInput : sentText;
}
